/**
 * The Sections view's one line being changed (slice 6d spec §8.5): its save
 * as the whole file from the version the page has, and a newer brief it ran
 * into, the typed text kept over it to apply again. The line is followed by
 * its text through others' live saves; once it's gone, that's a conflict.
 */
import { useState } from "react";
import { briefRefusal, conflictFile, useSaveBrief, type BriefFile } from "./brief-api";
import { afterSave, applyEdit, lineView, pinEdit, todayIn, type LineEdit } from "./brief";
import { toDraft, uniqueMention, withLinks, type Mention } from "./mentions";

/** The line being changed, its save, and a newer brief it ran into. */
export function useLineEditor(areaId: string, file: BriefFile, timeZone: string) {
  const save = useSaveBrief(areaId);
  const [opened, setEditing] = useState<LineEdit | null>(null);
  // The docs its `@Title`s name (spec §5.5): the line's own, and each picked.
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [clash, setConflict] = useState(false);
  const today = todayIn(timeZone);
  const view = lineView(file.content, opened, clash, today, (e) => withLinks(e, mentions)!);
  const write = (content: string, sent: LineEdit) => {
    save.mutate(
      { content, version: file.version },
      {
        // Typing during the save keeps the box open on the saved line, with the newer words.
        onSuccess: () => {
          setEditing((e) => afterSave(e, sent, content));
          setConflict(false);
        },
        // The typed text stays, over the brief as it is now, to apply again.
        onError: (error) => setConflict(conflictFile(error) !== null),
      },
    );
  };
  const reset = (edit: LineEdit | null) => {
    save.reset();
    setConflict(false);
    const read = toDraft(edit?.draft ?? "");
    setMentions(read.mentions);
    setEditing(edit && { ...edit, draft: read.draft });
  };
  return {
    editing: view.editing,
    conflict: view.conflict,
    busy: save.isPending,
    error: briefRefusal(save.error).problem,
    length: view.next.length,
    open: (edit: LineEdit) => reset(pinEdit(file.content, edit)),
    cancel: () => reset(null),
    change: (draft: string) => setEditing((e) => e && { ...e, draft }),
    /** A picked doc as its `@Title` will read: unlike the line's other mentions'. */
    named: (m: Mention) => uniqueMention(m, mentions),
    /** A doc picked for the `@…` being typed: its `@Title` now names it. */
    mention: (m: Mention, draft: string) => {
      setMentions((list) => [...list.filter((x) => x.title !== m.title), m]);
      setEditing((e) => e && { ...e, draft });
    },
    save: () => {
      if (view.saveable) write(view.next, view.live!);
    },
    remove: () => view.sent && write(applyEdit(file.content, view.sent, today, true), view.live!),
  };
}

export type LineEditor = ReturnType<typeof useLineEditor>;
